import type { LiveActivityRow } from '@/components/LiveActivityPanel';
import type { RunTraceRow } from '@/types/api';


const BULKY_TOOL_ARGS = new Set(['content', 'old_string', 'new_string']);

export const ARG_SEPARATOR = ' · ';

export function summaryParts(summary: string): string[] {
  if (summary === '') return [];
  return summary.split(ARG_SEPARATOR);
}

export function summariseToolArgs(rawArgs: string): string {
  if (typeof rawArgs !== 'string' || rawArgs.length === 0) return '';
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawArgs);
  } catch {
    return `<unparsed: ${rawArgs.slice(0, 120)}>`;
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return String(rawArgs).slice(0, 200);
  }
  const parts: string[] = [];
  for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
    if (value === null || value === undefined) continue;
    if (typeof value === 'string' && BULKY_TOOL_ARGS.has(key)) {
      parts.push(`${key}=<${value.length} chars>`);
      continue;
    }
    const rendered = typeof value === 'string' ? value : JSON.stringify(value);
    if (rendered === undefined) continue;
    parts.push(`${key}=${rendered.length > 200 ? rendered.slice(0, 200) + '…' : rendered}`);
  }
  return parts.join(' ');
}

export function panelArgs(rawArgs: string): string {
  const summarised = summariseToolArgs(rawArgs);
  if (summarised === '') return '';
  const single = /^([A-Za-z_][A-Za-z0-9_]*)=([\s\S]*)$/.exec(summarised);
  if (single && !/\s\w+=/.test(summarised)) return single[2] ?? summarised;
  return summarised;
}

export function describeActivity(
  type: string,
  payload: Record<string, unknown>
): { phase: 'tool_started' | 'tool_args' | 'turn_completed'; tool_names: string[]; summary: string } | null {
  try {
    if (type === 'response.output_item.added') {
      const item = payload['item'] as Record<string, unknown> | undefined;
      if (!item || item['type'] !== 'function_call') return null;
      const name = typeof item['name'] === 'string' ? item['name'] : 'tool';
      return { phase: 'tool_started', tool_names: [name], summary: '' };
    }
    if (type === 'response.function_call_arguments.done') {
      const args = typeof payload['arguments'] === 'string' ? payload['arguments'] : '';
      const name = typeof payload['name'] === 'string' ? payload['name'] : '';
      return {
        phase: 'tool_args',
        tool_names: name !== '' ? [name] : [],
        summary: panelArgs(args)
      };
    }
    if (type === 'response.completed') {
      return { phase: 'turn_completed', tool_names: [], summary: '' };
    }
    return null;
  } catch {
    return null;
  }
}

export function invocationLabel(invocation_key: string): string {
  const HYPHENATED_MODES = ['gen-tests', 'reviewer-rerun', 'structure-logs'] as const;
  let rest = invocation_key;
  let hyphenatedMode: string | undefined;
  for (const m of HYPHENATED_MODES) {
    if (rest.includes(`-m${m}`)) {
      hyphenatedMode = m;
      rest = rest.replace(`-m${m}`, '');
      break;
    }
  }
  const parts = rest.split('-');
  const stage = parts[0] ?? invocation_key;
  let mode: string | undefined = hyphenatedMode;
  let task: string | undefined;
  let episode: string | undefined;
  let attempt: string | undefined;
  let loop: string | undefined;
  for (const p of parts.slice(1)) {
    if (mode === undefined && p.startsWith('m') && !/^m?\d+$/.test(p)) mode = p.slice(1);
    else if (/^t\d+$/.test(p)) task = String(Number(p.slice(1)));
    else if (/^e\d+$/.test(p)) episode = p.slice(1);
    else if (/^r\d+$/.test(p)) attempt = p.slice(1);
    else if (/^s\d+$/.test(p)) loop = p.slice(1);
  }
  if (mode === 'rework' || mode === 'doubt') {
    const which = attempt
      ? `attempt ${attempt}`
      : loop
        ? `self-verify ${loop}`
        : undefined;
    const ep = episode ? ` · send-back ${episode}` : '';
    return `Rework${which ? ` — ${which}` : ''}${ep}`;
  }
  if (mode === 'gen-tests') return 'Test generation';
  if (mode === 'reviewer-rerun') return 'Review re-run';
  if (task) return `Task ${task}`;
  return stage;
}

export function rowKey(row: { turn: number; invocation_key: string }): string {
  return `${row.invocation_key}#${row.turn}`;
}

export function foldTraceRows(rows: ReadonlyArray<RunTraceRow>): LiveActivityRow[] {
  const out: LiveActivityRow[] = [];
  const acc = new Map<string, { turn: number; tool_names: string[]; args: string[] }>();

  for (const row of rows) {
    const activity = describeActivity(row.type, row.payload ?? {});
    if (activity === null) continue;

    const key = row.invocation_key;
    let state = acc.get(key);
    if (!state || state.turn !== row.turn) {
      state = { turn: row.turn, tool_names: [], args: [] };
      acc.set(key, state);
    }

    if (activity.phase === 'tool_started') {
      state.tool_names.push(...activity.tool_names);
    } else if (activity.phase === 'tool_args') {
      if (activity.summary !== '') state.args.push(activity.summary);
    } else {
      if (state.tool_names.length > 0 || state.args.length > 0) {
        out.push({
          stage_id: row.stage_id,
          invocation_key: row.invocation_key,
          turn: row.turn,
          tool_names: [...state.tool_names],
          summary: state.args.join(ARG_SEPARATOR),
          ts: row.ts
        });
      }
      acc.delete(key);
    }
  }

  return out;
}

export function mergeActivityRows(
  history: ReadonlyArray<LiveActivityRow>,
  live: ReadonlyArray<LiveActivityRow>
): LiveActivityRow[] {
  const byKey = new Map<string, LiveActivityRow>();
  for (const r of history) byKey.set(rowKey(r), r);
  for (const r of live) byKey.set(rowKey(r), r);
  return [...byKey.values()].sort((a, b) =>
    a.invocation_key === b.invocation_key
      ? a.turn - b.turn
      : a.invocation_key.localeCompare(b.invocation_key)
  );
}
